<template>
  <div class="dropdown-wrapper">
    <div
      v-if="!isContextMenu"
      ref="triggerContainer"
      @click="!triggerOnHover ? toggleDropdown() : null"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <slot
        v-if="$slots['triggerContent']"
        name="triggerContent"
      />
      <Button
        v-else
        :label="text"
        :severity="variant"
        is-dropdown
        @click.prevent
      />
    </div>

    <teleport to="#dropdown-overlay">
      <div>
        <ul
          v-if="isOpen"
          ref="dropdownMenu"
          v-click-away="handleClickAway"
          class="dropdown-menu"
          :style="[floatingStyles, menuStyles]"
          @mouseenter="handleMenuMouseEnter"
          @mouseleave="handleMenuMouseLeave"
        >
          <slot />
        </ul>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/vue'
import { computed, inject, nextTick, onBeforeUnmount, onMounted, provide, ref, watch } from 'vue'
import Button from '@/components/button/Button.vue'
import { useDropdownStack } from '@/composables/useDropdownStack.ts'

const props = defineProps({
  variant: { type: String, default: 'primary' },
  text: { type: String, default: null },
  position: { type: String, default: 'bottom-start' }, // left-start, left-end, right-start, right-end
  split: { type: Boolean, default: false },
  padding: { type: Boolean, default: true },
  closeOnClickAway: { type: Boolean, default: true },
  closeOnClickWithin: { type: Boolean, default: false },
  isDisabled: { type: Boolean, default: false },
  isContextMenu: { type: Boolean, default: false },
  triggerOnHover: { type: Boolean, default: false },
  menuMaxHeight: { type: String, default: null },
  space: { type: Number, default: undefined } // Distance in pixels between the dropdown and its trigger
})

const emit = defineEmits(['close'])

const isOpen = ref(false)
const triggerContainer = ref(null)
const dropdownMenu = ref(null)
const mousePosition = ref({ x: 0, y: 0 })
const hoverTimeout = ref(null)
const menuHoverTimeout = ref(null)

const dropdownId = Symbol('dropdown')

// Ancestor chain injection: obtain array of ancestor ids (default empty)
const parentAncestors = inject('dropdownAncestors', [])
const ancestorsWithSelf = [...parentAncestors, dropdownId]

// Provide current dropdown id and ancestors to potential children
provide('dropdownAncestors', ancestorsWithSelf)
provide('dropdownId', dropdownId)

// Global dropdown stack utilities
const { openDropdown, closeDropdown: unregisterDropdown, isTop } = useDropdownStack()

// Custom reference object to dynamically switch between cursor and container
const reference = computed(() =>
  props.isContextMenu
    ? {
        getBoundingClientRect: () => ({
          top: mousePosition.value.y,
          left: mousePosition.value.x,
          right: mousePosition.value.x + 1, // simulate a small width
          bottom: mousePosition.value.y + 1, // simulate a small height
          width: 0,
          height: 0
        })
      }
    : triggerContainer.value
)

const { floatingStyles, update } = useFloating(reference, dropdownMenu, {
  placement: props.position,
  middleware: [offset(props.space ?? (props.isContextMenu ? 10 : 5)), flip(), shift()],
  strategy: 'fixed',
  whileElementsMounted: autoUpdate
})

const menuStyles = computed(() => ({
  padding: props.padding ? 'var(--s-1)' : undefined,
  maxHeight: props.menuMaxHeight ?? undefined,
  overflowY: props.menuMaxHeight ? 'auto' : undefined
}))

function onContextMenu(event) {
  if (props.isContextMenu) {
    event.preventDefault()
    mousePosition.value = { x: event.clientX, y: event.clientY }
    nextTick(() => {
      isOpen.value = true
      openDropdown(
        dropdownId,
        () => {
          isOpen.value = false
          unregisterDropdown(dropdownId)
        },
        parentAncestors
      )
      update()
    })
  }
}

onMounted(() => {
  window.addEventListener('contextmenu', onContextMenu)
})

onBeforeUnmount(() => {
  window.removeEventListener('contextmenu', onContextMenu)
  if (hoverTimeout.value) {
    clearTimeout(hoverTimeout.value)
  }
  if (menuHoverTimeout.value) {
    clearTimeout(menuHoverTimeout.value)
  }
})

function toggleDropdown() {
  if (!props.isDisabled && !props.isContextMenu) {
    // Prevent normal toggle if context menu
    isOpen.value = !isOpen.value
    if (isOpen.value) {
      // Open this dropdown and close unrelated ones
      openDropdown(
        dropdownId,
        () => {
          isOpen.value = false
          unregisterDropdown(dropdownId)
        },
        parentAncestors
      )
    } else {
      unregisterDropdown(dropdownId)
    }
    nextTick(update)
  }
}

function closeDropdown() {
  emit('close')
  isOpen.value = false
  unregisterDropdown(dropdownId)
}

function open() {
  if (!props.isDisabled && !isOpen.value) {
    isOpen.value = true
    openDropdown(
      dropdownId,
      () => {
        isOpen.value = false
        unregisterDropdown(dropdownId)
      },
      parentAncestors
    )
    nextTick(update)
  }
}

provide('isOpen', isOpen)
provide('closeDropdown', closeDropdown)

// Expose methods to parent components
defineExpose({
  closeDropdown,
  open
})

// Handle click inside and outside of dropdown
function handleClickAway(event) {
  // The click-away directive *should* call this only when the target
  // is outside the menu. However, if the clicked element is removed
  // from the DOM (e.g. deleting a list item) before the check runs,
  // it can erroneously report as "outside". We guard against that by
  // double-checking the composed path for our menu element.

  const path = typeof event.composedPath === 'function' ? event.composedPath() : []
  if (dropdownMenu.value && (dropdownMenu.value.contains(event.target) || path.includes(dropdownMenu.value))) {
    // Click originated inside the dropdown; ignore.
    return
  }

  if (props.closeOnClickAway && isTop(dropdownId)) {
    closeDropdown()
  }
}

watch(isOpen, newVal => {
  if (!newVal) closeDropdown()
})

// Hover handlers
function handleMouseEnter() {
  if (props.triggerOnHover && !props.isDisabled && !props.isContextMenu) {
    // Clear any pending close timeout
    if (hoverTimeout.value) {
      clearTimeout(hoverTimeout.value)
      hoverTimeout.value = null
    }
    if (menuHoverTimeout.value) {
      clearTimeout(menuHoverTimeout.value)
      menuHoverTimeout.value = null
    }

    if (!isOpen.value) {
      isOpen.value = true
      openDropdown(
        dropdownId,
        () => {
          isOpen.value = false
          unregisterDropdown(dropdownId)
        },
        parentAncestors
      )
      nextTick(update)
    }
  }
}

function handleMouseLeave() {
  if (props.triggerOnHover) {
    // Delay closing to allow moving mouse to dropdown menu
    hoverTimeout.value = setTimeout(() => {
      if (isTop(dropdownId)) {
        closeDropdown()
      }
      hoverTimeout.value = null
    }, 100)
  }
}

function handleMenuMouseEnter() {
  if (props.triggerOnHover) {
    // Clear any pending close timeout when hovering over menu
    if (hoverTimeout.value) {
      clearTimeout(hoverTimeout.value)
      hoverTimeout.value = null
    }
    if (menuHoverTimeout.value) {
      clearTimeout(menuHoverTimeout.value)
      menuHoverTimeout.value = null
    }
  }
}

function handleMenuMouseLeave() {
  if (props.triggerOnHover) {
    // Delay closing when leaving menu
    menuHoverTimeout.value = setTimeout(() => {
      if (isTop(dropdownId)) {
        closeDropdown()
      }
      menuHoverTimeout.value = null
    }, 100)
  }
}
</script>

<style>
.dropdown-wrapper {
  position: relative;
  display: inline-block;
}

.dropdown-menu {
  list-style: none;
  border-radius: var(--rounded-lg);
  box-shadow: var(--shadow-lg);
  background-color: var(--bg-primary);
  background-clip: padding-box;
  border: 1px solid var(--border-secondary);
  min-width: 10rem;
  font-size: 1rem;
}
</style>
